import { ApolloError } from '@apollo/react-hooks';
import { useCreateDirectory } from '@netfront/ekardo-content-library';
import { Breadcrumbs, IBreadcrumbItem } from '@netfront/ui-library';
import kebabCase from 'lodash.kebabcase';
import { useRouter } from 'next/router';

import { Affiliates } from '../../../components/Affiliates';
import { BreadcrumbItemDirectoryPageLink, BreadcrumbItemHomePageLink, BreadcrumbItem } from '../../../components/BreadcrumbItems';
import { BUTTON_CLASSES } from '../../../components/Button';
import { DirectoryForm, DirectoryHeader } from '../../../components/Directory';
import { HintBox } from '../../../components/HintBox';
import { PageLayout } from '../../../components/PageLayout';
import { useToast } from '../../../hooks/useToast';

const PAGE_TITLE = 'Register your service';

const BREADCRUMB_ITEMS: IBreadcrumbItem[] = [
  {
    content: <BreadcrumbItemHomePageLink />,
    key: 'home',
  },
  {
    content: <BreadcrumbItemDirectoryPageLink />,
    key: 'directory',
  },
  {
    content: <BreadcrumbItem text={PAGE_TITLE} />,
    key: kebabCase(PAGE_TITLE),
  },
];

const AddServicePage = () => {
  const { push } = useRouter();
  const { handleToastError } = useToast();

  const handleCreateDirectoryCompleted = (): void => {
    push('/directory/add-service/thank-you').catch((error) =>
      handleToastError({
        shouldUseFriendlyErrorMessage: true,
        error,
      }),
    );
  };

  const handleCreateDirectoryError = (error: ApolloError): void => {
    handleToastError({
      shouldUseFriendlyErrorMessage: true,
      error,
    });
  };

  const { handleCreateDirectory, isLoading = false } = useCreateDirectory({
    onCompleted: handleCreateDirectoryCompleted,
    onError: handleCreateDirectoryError,
  });

  return (
    <PageLayout isPreloaderVisible={isLoading} title={PAGE_TITLE}>
      <div className="container-md flex justify-end py-8">
        <Breadcrumbs items={BREADCRUMB_ITEMS} />
      </div>

      <DirectoryHeader subHeading="Register your service" />

      <section className="container-md pb-12">
        <HintBox>
          <p>
            Make it easier for brain cancer family caregivers to find and access the available services for themselves and their loved one.
            By adding your service here, our team at CarerWell can make it available to caregivers via our service directory.
          </p>
          <p>Please include an overview of your service and information about referral pathways in the service description section.</p>
          <p>You will receive a confirmation email once your service has been approved and added to the service locator.</p>
        </HintBox>
      </section>

      <section className="container-md pb-8">
        <div className="flex flex-col md:flex-row">
          <div className="mb-12 md:mb-0 md:mr-24 w-full">
            <DirectoryForm
              buttonClassName={BUTTON_CLASSES.default}
              isSubmitting={isLoading}
              onSubmit={({
                contactEmail,
                contactNumber,
                postcode,
                serviceDescription,
                siteOrganisationName,
                state,
                street,
                suburb,
                websiteUrl,
              }) => {
                handleCreateDirectory({
                  address: {
                    country: 'AU',
                    line1: street,
                    postcode: String(postcode),
                    state,
                    suburb,
                  },
                  directory: {
                    description: serviceDescription,
                    directoryTypeKey: 'SERVICES',
                    email: contactEmail,
                    phoneNumber: contactNumber,
                    projectId: String(process.env.REACT_APP_PROJECT_ID),
                    status: 'PENDING',
                    title: siteOrganisationName,
                    url: websiteUrl,
                  },
                });
              }}
            />
          </div>
        </div>
      </section>

      <Affiliates />
    </PageLayout>
  );
};

// eslint-disable-next-line import/no-default-export
export default AddServicePage;
